import { BlockProps, CustomImageProps } from "@/utils/types";
import { BlockWrapper } from "./BlockWrapper";
import { sanitize } from "@/utils/sanitize";
import { ImageObject } from "../modules/ImageObject";
import { SvgSelector } from "../utility/SvgSelector";
import Link from "next/link";
import { Heading } from "../modules/Heading";
import { getFirstName } from "@/utils/getFirstName";

export interface PeopleProps extends BlockProps {
  peopleList: {
    _id: string;
    name: string;
    role: string;
    email?: string;
    image: CustomImageProps;
  }[];
  extraNames?: {
    heading: string;
    names: {
      _key: string;
      name: string;
      role?: string;
    }[];
  };
}

export const People = ({
  backgroundColor = "bg-white",
  bottom,
  className,
  colorStarter,
  componentId,
  heading,
  extraNames,
  peopleList,
  primaryEnder,
}: PeopleProps) => {
  const lightMode =
    sanitize(backgroundColor) === "bg-white" ||
    sanitize(backgroundColor) === "bg-secondary" ||
    sanitize(backgroundColor) === "bg-tertiary";

  const hasWave = sanitize(backgroundColor) === "waves";
  const hasPattern = sanitize(backgroundColor) === "pattern";

  const headingClasses =
    sanitize(backgroundColor) === "waves" || lightMode
      ? "text-secondaryDark"
      : "text-white";

  const darkWave =
    sanitize(backgroundColor) === "bg-secondary" ||
    sanitize(backgroundColor) === "bg-tertiary" ||
    sanitize(backgroundColor) === "pattern";

  const blockProps = {
    backgroundColor,
    bottom,
    colorStarter,
    componentId,
    heading,
    lightMode,
    primaryEnder,
  };

  return (
    <BlockWrapper
      {...blockProps}
      className={`people${className ? ` ${className}` : ""}`}
    >
      <div className="contained">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-10">
          {peopleList?.map(
            (person, i) =>
              person && (
                <div
                  key={person._id + i}
                  className="flex flex-col items-center gap-4 w-full"
                >
                  <div
                    className={`border-[3px] rounded-[24px] md:rounded-card  overflow-hidden  duration-[0.1s] relative h-[323px] w-full ${
                      sanitize(backgroundColor) === "bg-secondary"
                        ? "border-primary"
                        : "border-secondary "
                    }`}
                  >
                    {person.image && person.image.imageFor && (
                      <ImageObject
                        {...person.image}
                        imageWidth={400}
                        className="object-cover absolute w-full h-full"
                      />
                    )}
                  </div>
                  <div
                    className={`text-center font-bold ${
                      hasWave
                        ? "bg-white p-2 rounded-lg shadow-md text-black"
                        : hasPattern
                          ? "bg-secondary p-2 rounded-lg shadow-md text-black"
                          : lightMode
                            ? "text-black"
                            : "text-white"
                    }`}
                  >
                    <p className="uppercase font-extrabold text-lg">
                      {person.name}
                    </p>
                    <p className="font-bold lowercase leading-[1.2]">
                      {person.role}
                    </p>
                    {person.email && (
                      <Link
                        href={`mailto:${person.email}`}
                        className="flex items-center gap-2 mt-4 text-primaryDark hover:text-white lowercase justify-center px-2"
                      >
                        <SvgSelector name="Envelope" />
                        email {getFirstName(person.name)}
                      </Link>
                    )}
                  </div>
                </div>
              )
          )}
        </div>
        {extraNames && extraNames.names.length > 0 && (
          <>
            {extraNames.heading && (
              <Heading
                copy={extraNames.heading}
                numberOfWaves={5}
                waveColor={darkWave ? "#540389" : undefined}
                lineThickness={5}
                className={`${headingClasses} z-[2] mt-24`}
              />
            )}
            <div className="flex justify-center items-start flex-wrap gap-24">
              {extraNames.names.map((name) => (
                <p
                  key={name._key}
                  className={`flex flex-col text-center font-bold ${
                    hasWave
                      ? "bg-white p-2 rounded-lg shadow-md text-black"
                      : hasPattern
                        ? "bg-secondary p-2 rounded-lg shadow-md text-black"
                        : lightMode
                          ? "text-black"
                          : "text-white"
                  }`}
                >
                  <span className="text-extrabold">{name.name}</span>
                  {name.role && <span className="lowercase">{name.role}</span>}
                </p>
              ))}
            </div>
          </>
        )}
      </div>
    </BlockWrapper>
  );
};
